import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import { type RefSelectProps } from "antd/lib/select";
import { type RefTreeSelectProps } from "antd/lib/tree-select";
import { memo, type ReactNode, useCallback, useContext, useMemo, useRef } from "react";

import { cn } from "@btnet/utilities-core";

import { BdsSelect } from "@buildertrend/components";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { type BTSelectItem } from "types/apiResponse/apiResponse";

import { track, useTracking } from "utilities/analytics/analytics";
import { getByValue, isItemSelected } from "utilities/form/form";

import { isValidUserId } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.utils";
import { BTSelect, type BTSelectProps } from "commonComponents/btWrappers/BTSelect/BTSelect";
import {
    useBtSelectUserCustomItemRender,
    useBtSelectUserDropdownRender,
    useBtSelectUserTagRender,
} from "commonComponents/btWrappers/BTSelectUser/btSelectUser.utilities";

import "./BTSelectUser.less";

export interface IBTSelectUserProps<FormValues, ExtraDataType>
    extends Omit<BTSelectProps<FormValues, ExtraDataType>, "fieldRef"> {
    showAssignToMe?: boolean;
    /**
     * Adds a react node after the user in the dropdown
     */
    addonAfterUser?: (userId: BTSelectItem<ExtraDataType>) => ReactNode;
    useBds?: boolean;
    noTags?: boolean;
}

const BTSelectUserInternal = track({
    component: "User Select",
})(function <FormValues = undefined, ExtraDataType = undefined>({
    customItemRender,
    multiple = false,
    treeData,
    value,
    readOnly,
    "data-testid": dataTestId,
    id,
    onChange,
    disabled,
    showAssignToMe = true,
    addonAfterUser,
    useBds,
    noTags = false,
    dropdownClassName,
    ...otherProps
}: IBTSelectUserProps<FormValues, ExtraDataType>) {
    const userInfo = useContext(UserInfoContext);

    const handleCustomItemRender = useBtSelectUserCustomItemRender(false, useBds, addonAfterUser);
    const handleTagRender = useBtSelectUserTagRender();

    const selectRef = useRef<RefSelectProps | RefTreeSelectProps>(null);

    const { trackEvent } = useTracking();

    const treeDataWithoutMeGroup = useMemo(() => {
        return treeData?.filter((node) => node.title?.toString() !== "Me");
    }, [treeData]);

    const currentUserItem = useMemo(
        () => getByValue(treeDataWithoutMeGroup, userInfo?.globalUserId),
        [treeDataWithoutMeGroup, userInfo],
    );

    const handleAssignToMeButtonClicked = useCallback(() => {
        if (currentUserItem) {
            onChange(id as string, currentUserItem?.id, currentUserItem);
            selectRef.current?.blur();
            trackEvent({
                event: "ValueChange",
                uniqueId: "assignedTo-assignToMe",
                value: currentUserItem?.id,
            });
        }
    }, [onChange, trackEvent, currentUserItem, id]);

    const handleAssignToMeChecked = useCallback(
        (e: CheckboxChangeEvent) => {
            if (currentUserItem) {
                let newValue;
                if (e.target.checked) {
                    newValue = multiple
                        ? Array.from(
                              value
                                  ? new Set([...value, currentUserItem.id])
                                  : new Set([currentUserItem.id]),
                          )
                        : currentUserItem.id;
                } else {
                    newValue = multiple
                        ? value.filter(
                              (v: { toString: () => string }) =>
                                  v.toString() !== currentUserItem.id.toString(),
                          )
                        : null;
                }

                onChange(id as string, newValue, currentUserItem);
                trackEvent({
                    event: "ValueChange",
                    uniqueId: "assignedTo-assignToMe",
                    value: currentUserItem?.id,
                });
            }
        },
        [id, onChange, trackEvent, value, multiple, currentUserItem],
    );

    const isCurrentUserSelected = isItemSelected(value, currentUserItem?.value);
    const handleDropdownRender = useBtSelectUserDropdownRender({
        currentUserItem,
        handleAssignToMeButtonClicked,
        handleAssignToMeChecked,
        isCurrentUserSelected,
        multiple,
        showAssignToMe,
    });
    const SelectComponent = useBds ? BdsSelect : BTSelect;

    return (
        <SelectComponent
            {...otherProps}
            id={id}
            fieldRef={selectRef}
            onChange={onChange}
            readOnly={readOnly}
            disabled={disabled}
            treeData={treeDataWithoutMeGroup}
            data-testid={dataTestId}
            customItemRender={handleCustomItemRender}
            multiple={multiple}
            value={value}
            className={cn("BTSelectUser", otherProps.className, {
                BTSelectUserCursorPadding:
                    multiple === false && value !== undefined && isValidUserId(value),
            })}
            dropdownClassName={cn("BTSelectUserDropdown", dropdownClassName)}
            maxHeight="large"
            tagRender={noTags ? () => <></> : handleTagRender}
            dropdownRender={handleDropdownRender}
        />
    );
});

export const BTSelectUser = memo(BTSelectUserInternal) as typeof BTSelectUserInternal;
